import type { IntlShape } from 'react-intl'
import type { Dispatch } from 'react'

import { initAccommodationV2Sessions } from 'actions/AccommodationSessionActions'
import { addToCheckoutCart, initBedbankSession, initialiseCheckout } from 'actions/CheckoutActions'
import { fetchPropertyRates } from 'actions/PropertyActions'
import { addToUserCart } from 'actions/UserCartActions'
import { trackEvent } from 'analytics/analytics'
import { addPropertyRatesToCartGTM } from 'analytics/eventDefinitions'
import { addPropertyRatesToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import { FlightViewTypes } from 'constants/flight'
import type { ModalContextType } from 'contexts/ModalContext'
import type { ModalElementContextValue } from 'contexts/ModalProvider'
import type { OfferPageDispatchAction, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isAccommodationV2Booking } from 'lib/accommodation/isAccommodationV2Booking'
import {
  generateAccommodationV2CheckoutItem,
  generateBedbankPropertyAccommodationCheckoutItems,
  generateLpcPropertyAccommodationCheckoutItems,
} from 'lib/checkout/accommodation/cart'
import { generateExperiencesPassportCheckoutItems } from 'lib/checkout/experiences/cart'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { floatify } from 'lib/maths/mathUtils'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isBedbankRate, isLpcRate } from 'lib/offer/propertyTypes'
import type { AppDispatch } from 'src/client/store'
import type { ExperiencesPassportCheckoutSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'

import {
  createBundledCheckoutItem,
  validateBundledPropertyRateForCheckout,
  validateOptInBundlesForCheckout,
} from './propertyBuyCallback'

export interface PropertySelectedRateByRoom {
  room: OfferPageRoom
  rate: App.EnrichedPropertyRate
  selectedOptionalBundles?: Array<App.Property.OptionalBundle>
}

interface MultiRoomArgs {
  shouldAddToCart: boolean
  dispatch: AppDispatch
  currentCurrency: string
  airports?: Array<App.Airport>
  memberId?: string
  offerPageDispatch: Dispatch<OfferPageDispatchAction>
  modalElementContext: ModalElementContextValue<never>
  showModal: ModalContextType
  intl: IntlShape
  property: App.Property.Property
  selectedRatesByRoom: Array<PropertySelectedRateByRoom>
  originAirportCode: string | undefined
  flightQuotedPrice: number | undefined
  checkIn: string
  checkOut: string
  duration: number
  regionCode: string
  source: string
  utmContent?: string
  isSpoofed: boolean
  isLoggedIn: boolean
  isAccommodationV2BookingsEnabled: boolean
  subscriber: App.OrderSubscriber | undefined
  experiencesPassportCheckoutSourcesByRoom?: Record<string, Array<ExperiencesPassportCheckoutSource>>
  isLuxPlusMember: boolean
}

export async function propertyMultiRoomBookNowOrAddToCartCallback(args: MultiRoomArgs) {
  const {
    shouldAddToCart,
    dispatch,
    currentCurrency,
    airports,
    memberId,
    offerPageDispatch,
    modalElementContext,
    showModal,
    intl,
    property,
    selectedRatesByRoom,
    originAirportCode,
    flightQuotedPrice,
    checkIn,
    checkOut,
    duration,
    regionCode,
    source,
    utmContent,
    isSpoofed,
    isLoggedIn,
    isAccommodationV2BookingsEnabled,
    subscriber,
    isLuxPlusMember,
  } = args

  if (selectedRatesByRoom.length === 0) {
    return
  }

  const period = { checkIn, checkOut, duration }
  const useAccommodationV2 = isAccommodationV2Booking({
    property,
    isLoggedIn,
    isAccommodationV2BookingsEnabled,
    source,
    utmContent,
  })
  const accommodationItems: Array<
    | App.Checkout.InstantBookingLEHotelItem
    | App.Checkout.BedbankHotelItem
    | App.Checkout.AccommodationV2Item
  > = []
  const flightItems: Array<App.Checkout.FlightItem> = []
  const bundledExperienceItems: Array<App.Checkout.ExperienceItem | App.Checkout.TransferItem> = []
  const experiencesPassportItems: Array<App.Checkout.ExperienceItem> = []
  const bundledItemIdsByRoomId: Record<string, Array<string>> = {}

  let closeProcessingModal: undefined | (() => void)
  const closeOpenProcessingModal = () => {
    closeProcessingModal?.()
    closeProcessingModal = undefined
  }

  const showProcessingModal = () => {
    if (closeProcessingModal) {
      return
    }
    showModal((resolve) => {
      closeProcessingModal = resolve
      return <ProcessingModal message={intl.formatMessage({
        id: '65JLQI9bUAc3Orze8tm-',
        defaultMessage: 'Please wait...',
        description: 'Processing modal message when initializing bedbank session',
      })}
      />
    })
  }

  if (
    selectedRatesByRoom.some(({ rate, selectedOptionalBundles }) => !!rate.bundle || !!selectedOptionalBundles?.length) ||
    (!shouldAddToCart && (
      useAccommodationV2 ||
      selectedRatesByRoom.some(({ rate }) => isBedbankRate(rate))
    ))
  ) {
    showProcessingModal()
  }

  // Refresh every occupancy room, not just the one that failed: a sold-out rate usually means our
  // rates are stale, and refreshing only the failing room leaves the others able to fail next.
  const refreshRatesAndShowSoldOut = () => {
    dispatch(fetchPropertyRates(property.id, {
      checkIn,
      checkOut,
      occupancies: selectedRatesByRoom.map(({ room }) => room),
      utmContent: undefined,
      roomTypeId: undefined,
      roomRateId: undefined,
      refresh: true,
    }))
    showModal(<BedbankSoldOutModal />)
  }

  for (const { room, rate, selectedOptionalBundles = [] } of selectedRatesByRoom) {
    if (!rate.bundle && !selectedOptionalBundles.length) continue
    const isValid = await validateBundledPropertyRateForCheckout({
      checkIn,
      checkOut,
      rooms: [room],
      regionCode,
      source,
      rate,
    }) && await validateOptInBundlesForCheckout({
      checkIn,
      checkOut,
      rooms: [room],
      regionCode,
      source,
      rate,
      optionalBundles: selectedOptionalBundles,
    })
    if (!isValid) {
      closeOpenProcessingModal()
      refreshRatesAndShowSoldOut()
      return
    }
  }

  let v2SessionIds: Array<string> | undefined
  if (useAccommodationV2 && !shouldAddToCart) {
    try {
      v2SessionIds = await initAccommodationV2Sessions(dispatch, {
        selections: selectedRatesByRoom.map(({ room, rate, selectedOptionalBundles }) => ({
          rate,
          occupancy: room,
          ...(selectedOptionalBundles?.length && { selectedOptionalBundles }),
        })),
        checkIn,
        checkOut,
        region: regionCode,
        ...(!!subscriber?.tier && { isLuxPlusUser: true }),
      })
    } catch {
      closeOpenProcessingModal()
      refreshRatesAndShowSoldOut()
      return
    }
  }

  for (const [index, selection] of selectedRatesByRoom.entries()) {
    const { room, rate } = selection
    const roomOccupancies = [room]
    const selectedOptionalBundles = selection.selectedOptionalBundles ?? []
    let roomItems: Array<
      | App.Checkout.InstantBookingLEHotelItem
      | App.Checkout.BedbankHotelItem
      | App.Checkout.AccommodationV2Item
    > = []

    if (useAccommodationV2) {
      const roomItem = generateAccommodationV2CheckoutItem(
        rate,
        period,
        room,
        v2SessionIds?.[index],
        undefined,
        property.airportCode,
        selectedOptionalBundles,
      )
      roomItems = [roomItem]
      accommodationItems.push(roomItem)
      bundledItemIdsByRoomId[room.id] = [roomItem.itemId]
    } else if (isLpcRate(rate)) {
      roomItems = generateLpcPropertyAccommodationCheckoutItems(rate, period, roomOccupancies, undefined, selectedOptionalBundles)
      accommodationItems.push(...roomItems)
      bundledItemIdsByRoomId[room.id] = roomItems.map(({ itemId }) => itemId)
    } else if (isBedbankRate(rate)) {
      const bedbankRate: App.EnrichedBedbankPropertyRate = rate
      let sessionId: string | undefined
      if (!shouldAddToCart) {
        try {
          const cheapestFlightPrice = typeof bedbankRate.cheapestFlightPrice === 'number' ? bedbankRate.cheapestFlightPrice : 0
          // `baseSellingPrice`, not `sellingPrice` — see the same branch in `propertyBuyCallback`:
          // sending svc-bedbank the marked price marks the rate a second time at checkout.
          const [hotelSellingPrice, hotelLuxPlusSellingPrice] = bedbankRate.bundle ? [
            bedbankRate.bundle.hotelSellingPrice,
            bedbankRate.bundle.hotelLuxPlusSellingPrice || undefined,
          ] : [
            floatify(bedbankRate.totals.baseSellingPrice - cheapestFlightPrice),
            bedbankRate.luxPlusTotals?.baseSellingPrice ? floatify(bedbankRate.luxPlusTotals.baseSellingPrice - cheapestFlightPrice) : undefined,
          ]
          if (!bedbankRate.service.bedGroups?.[0]) {
            closeOpenProcessingModal()
            refreshRatesAndShowSoldOut()
            return
          }
          const session = await initBedbankSession(dispatch, {
            propertyId: bedbankRate.service.propertyId,
            roomTypeId: bedbankRate.service.roomId,
            roomRateId: bedbankRate.service.rateId,
            token: bedbankRate.service.token,
            rateGroupId: bedbankRate.service.rateGroupId,
            bedGroupId: bedbankRate.service.bedGroups[0].id,
            isFlightBundle: bedbankRate.isFlightBundle,
            csBooking: isSpoofed,
            region: regionCode,
            rooms: roomOccupancies,
            checkIn,
            checkOut,
            price: hotelSellingPrice,
            propertyFees: bedbankRate.totals.propertyFees,
            memberInclusive: hotelLuxPlusSellingPrice,
            subscriber,
            source,
          })

          if (!('sessionId' in session)) {
            closeOpenProcessingModal()
            refreshRatesAndShowSoldOut()
            return
          }

          sessionId = session.sessionId
        } catch {
          closeOpenProcessingModal()
          refreshRatesAndShowSoldOut()
          return
        }
      }

      roomItems = generateBedbankPropertyAccommodationCheckoutItems(
        sessionId,
        bedbankRate,
        period,
        roomOccupancies,
        // Free package upgrades are single-room only, so multi-room never carries one.
        undefined,
        property.airportCode,
        selectedOptionalBundles,
      )
      accommodationItems.push(...roomItems)
      bundledItemIdsByRoomId[room.id] = roomItems.map(({ itemId }) => itemId)
    }

    const experiencesPassportCheckoutSources = args.experiencesPassportCheckoutSourcesByRoom?.[room.id] ?? []
    if (experiencesPassportCheckoutSources.length) {
      experiencesPassportItems.push(...generateExperiencesPassportCheckoutItems(roomItems, experiencesPassportCheckoutSources))
    }

    const bundle = rate.bundle
    if (bundle || selectedOptionalBundles.length) {
      const accommodationItemIds = bundledItemIdsByRoomId[room.id] ?? []
      const bundledTickets: Array<{ bundleId: string, bundleOptin?: boolean, ticket: { experienceId: string; productId: string; price: number } }> = [
        ...(bundle?.tickets ?? []).map(ticket => ({ bundleId: bundle!.bundleId, ticket })),
        ...selectedOptionalBundles.flatMap(optionalBundle => optionalBundle.tickets.map(({ experienceId, productId, price }) => ({
          bundleId: optionalBundle.bundleId,
          bundleOptin: true,
          ticket: { experienceId, productId, price },
        }))),
      ]
      bundledExperienceItems.push(...await Promise.all(bundledTickets.map(({ bundleId, bundleOptin, ticket }) =>
        createBundledCheckoutItem(accommodationItemIds, bundleId, bundleOptin, ticket, { checkIn, checkOut }, roomOccupancies, property, {
          currentCurrency,
          airports,
          currentRegionCode: regionCode,
          memberId,
        }),
      )))
    }
  }

  if (selectedRatesByRoom.some(({ rate }) => rate.includesFlights)) {
    if (!(originAirportCode && typeof flightQuotedPrice === 'number')) {
      closeOpenProcessingModal()
      throw new Error('Attempted to perform cart action on rate with flights when no flight pricing is available')
    }
    const rooms = selectedRatesByRoom.map(({ room }) => room)
    const flightOccupants = countOccupantsForFlights(rooms)
    flightItems.push(generateFlightCheckoutItem({
      originAirportCode,
      destinationAirportCode: property.airportCode,
      occupants: flightOccupants,
      passengers: generatePassengersFromOccupants(flightOccupants),
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      bundledItemIds: accommodationItems.map(({ itemId }) => itemId),
      quotedFare: flightQuotedPrice,
    }))
  }

  closeOpenProcessingModal()

  const checkoutItems: Array<App.Checkout.AnyItem> = [...accommodationItems, ...flightItems, ...bundledExperienceItems, ...experiencesPassportItems]
  offerPageDispatch({ type: OfferPageActions.closeFilter })
  modalElementContext?.resolve()

  if (flightItems.length > 0) {
    dispatch(initialiseCheckout(checkoutItems, { mode: 'vertical-select' }))
  } else if (!shouldAddToCart) {
    dispatch(addToCheckoutCart(checkoutItems))
  }

  // Single conversion event with N item contexts; N separate events would over-attribute the booking.
  fireEcommerceGTMEvent(addPropertyRatesToCartGTM(
    property,
    selectedRatesByRoom.map(({ room, rate }) => ({ rate, quantity: sumUpOccupancies([room]) })),
  ))
  trackEvent(addPropertyRatesToCart(
    selectedRatesByRoom.map(({ room, rate }) => ({ rate, occupancy: [room] })),
    { checkIn, checkOut, isLuxPlusMember, staysOnPage: shouldAddToCart && flightItems.length === 0 },
  ))

  if (flightItems.length > 0) {
    dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    return
  }
  if (shouldAddToCart) {
    dispatch(addToUserCart(checkoutItems))
    return
  }
  dispatch(navigateToCheckout())
}
